/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { Element, Root } from 'hast';
import { h } from 'hastscript';
import { renderToStaticMarkup } from 'react-dom/server';
import rehypeParse from 'rehype-parse';
import rehypeStringify from 'rehype-stringify';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';

import { rehypePluginCleanTree } from '../rehypePluginCleanTree/rehypePluginCleanTree';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { CKEditor5Preview } from '../../components/CKEditor5Preview/CKEditor5Preview';
import { CKBOX_URL } from '@ckeditor/ckeditor5-metadata';
import {
	CKEDITOR5_URL,
	CKEDITOR5_PREMIUM_FEATURES_URL,
	CKEDITOR5_CSS_URL,
	CKEDITOR5_PREMIUM_FEATURES_CSS_URL
} from '../../constants/cdns';
import { createImportMap } from '../createImportMap/createImportMap';
import { groupByInstallPackage } from '../groupByInstallPackage/groupByInstallPackage';
import { areTranslationsNeeded } from '../translations/translations';
import { getFilename } from '../getFilename/getFilename';
import type { GetCodeHtmlOptions } from '../getProjectFiles/types';

const icons = [
	{ rel: 'icon', type: 'image/png', href: 'https://ckeditor.com/assets/images/favicons/32x32.png', sizes: '32x32' },
	{ rel: 'icon', type: 'image/png', href: 'https://ckeditor.com/assets/images/favicons/96x96.png', sizes: '96x96' },
	{ rel: 'apple-touch-icon', type: 'image/png', href: 'https://ckeditor.com/assets/images/favicons/120x120.png', sizes: '120x120' },
	{ rel: 'apple-touch-icon', type: 'image/png', href: 'https://ckeditor.com/assets/images/favicons/152x152.png', sizes: '152x152' },
	{ rel: 'apple-touch-icon', type: 'image/png', href: 'https://ckeditor.com/assets/images/favicons/167x167.png', sizes: '167x167' },
	{ rel: 'apple-touch-icon', type: 'image/png', href: 'https://ckeditor.com/assets/images/favicons/180x180.png', sizes: '180x180' }
];

export function getCodeHtml( getCodeHtmlOptions: GetCodeHtmlOptions ): string {
	const { editorPlugins, editorType, toolbarOptions } = getCodeHtmlOptions.editorOptions;

	const staticMarkup = renderToStaticMarkup(
		<CKEditor5Preview
			locationOrigin=''
			editorPlugins={ editorPlugins }
			editorType={ editorType }
			toolbarOptions={ toolbarOptions }
		/>
	);

	return unified()
		.use( rehypeParse )
		.use( rehypePluginAttachSnippetSelector )
		.use( rehypePluginCleanTree )
		.use( rehypeMakeDocument )
		.use( rehypePluginAddHeadTags, getCodeHtmlOptions )
		.use( rehypePluginAddStyles, getCodeHtmlOptions )
		.use( rehypePluginAddScripts, getCodeHtmlOptions )
		.use( rehypeStringify )
		.processSync( staticMarkup )
		.toString()
		.trim();
}

function rehypePluginAddScripts( getCodeHtmlOptions: GetCodeHtmlOptions ) {
	const { selectedInstallMethod, includeServerWarning } = getCodeHtmlOptions;
	const { editorPlugins, editorLanguage } = getCodeHtmlOptions.editorOptions;

	return ( tree: Root ) => {
		visit( tree, 'element', node => {
			if ( node.tagName !== 'body' ) {
				return;
			}

			const hasPremiumPlugins = hasEditorPremiumPlugins( editorPlugins );

			if ( selectedInstallMethod === 'cdn' ) {
				const ckeditor5Path = `${ CKEDITOR5_URL }/ckeditor5.umd.js`;
				const ckeditor5PremiumPath = `${ CKEDITOR5_PREMIUM_FEATURES_URL }/ckeditor5-premium-features.umd.js`;

				node.children.push( h( 'script', { src: ckeditor5Path, crossorigin: true } ) );

				if ( hasPremiumPlugins ) {
					node.children.push( h( 'script', { src: ckeditor5PremiumPath, crossorigin: true } ) );
				}

				if ( areTranslationsNeeded( editorLanguage ) ) {
					const ckeditor5TranslationPath = `${ CKEDITOR5_URL }/translations/${ editorLanguage }.umd.js`;
					const ckeditor5PremiumTranslationPath = `${ CKEDITOR5_PREMIUM_FEATURES_URL }/translations/${ editorLanguage }.umd.js`;

					node.children.push( h( 'script', { src: ckeditor5TranslationPath, crossorigin: true } ) );

					if ( hasPremiumPlugins ) {
						node.children.push( h( 'script', { src: ckeditor5PremiumTranslationPath, crossorigin: true } ) );
					}
				}
			} else if ( selectedInstallMethod === 'zip' ) {
				node.children.push(
					h( 'script', { type: 'importmap' }, [ {
						type: 'text',
						value: createImportMap( { hasPremiumPlugins } )
					} ] )
				);
			}

			if ( editorPlugins.includes( 'CKBox' ) ) {
				node.children.push(
					h( 'script', { src: CKBOX_URL, crossorigin: true } )
				);
			}

			node.children.push(
				h( 'script', getJsScriptTag( getCodeHtmlOptions ) )
			);

			if ( includeServerWarning ) {
				node.children.push(
					{ type: 'comment', value: ' A friendly reminder to run on a server, remove this during the integration. ' },
					h( 'script', [
						{ type: 'text', value: [
							'window.onload = function() {',
							'	if ( window.location.protocol === "file:" ) {',
							'		alert( "This sample requires an HTTP server. Please serve this file with a web server." );',
							'	}',
							'};'
						].join( '\n' ) }
					] )
				);
			}
		} );
	};
}

function rehypeMakeDocument() {
	return ( tree: Root ) => {
		tree.children.unshift( { type: 'doctype' } );

		visit( tree, 'element', node => {
			if ( node.tagName !== 'html' ) {
				return;
			}

			node.properties.lang = 'en';
		} );
	};
}

function rehypePluginAddHeadTags( { includeFavicons }: GetCodeHtmlOptions ) {
	return ( tree: Root ) => {
		visit( tree, 'element', node => {
			if ( node.tagName !== 'head' ) {
				return;
			}

			node.children.push(
				h( 'meta', { charset: 'utf-8' } )
			);

			node.children.push(
				h( 'title', [ 'CKEditor 5 Sample' ] )
			);

			if ( includeFavicons ) {
				icons.forEach( icon => {
					node.children.push(
						h( 'link', icon )
					);
				} );
			}
		} );
	};
}

function rehypePluginAddStyles( { editorOptions, selectedInstallMethod, selectedTechnology }: GetCodeHtmlOptions ) {
	const isZip = selectedInstallMethod === 'zip';

	const ckeditor5CssUrl = isZip ?
		'./ckeditor5/ckeditor5.css' :
		CKEDITOR5_CSS_URL;
	const ckeditor5PremiumCssUrl = isZip ?
		'./ckeditor5-premium-features/ckeditor5-premium-features.css' :
		CKEDITOR5_PREMIUM_FEATURES_CSS_URL;

	return ( tree: Root ) => {
		visit( tree, 'element', node => {
			if ( selectedInstallMethod === 'npm' ) {
				return;
			}

			const cssFilename = getFilename( { type: 'css', selectedTechnology } );

			if ( node.tagName !== 'head' ) {
				return;
			}

			node.children.push(
				h( 'link', { rel: 'stylesheet', href: './' + cssFilename } )
			);

			node.children.push(
				h( 'link', { rel: 'stylesheet', href: ckeditor5CssUrl, crossorigin: !isZip } )
			);

			if ( hasEditorPremiumPlugins( editorOptions.editorPlugins ) ) {
				node.children.push(
					h( 'link', { rel: 'stylesheet', href: ckeditor5PremiumCssUrl, crossorigin: !isZip } )
				);
			}
		} );
	};
}

function rehypePluginAttachSnippetSelector() {
	return ( tree: Root ) => {
		visit( tree, 'element', node => {
			if ( node.properties.dataSnippetSelector ) {
				assignSnippetSelector( node );
				delete node.properties.dataSnippetSelector;
			}
		} );
	};
}

function hasEditorPremiumPlugins( editorPlugins: EditorPlugins ) {
	const { 'ckeditor5-premium-features': ckeditor5PremiumFeatures } = groupByInstallPackage( editorPlugins );

	return ckeditor5PremiumFeatures.length > 0;
}

function assignSnippetSelector( node: Element ) {
	const snippetSelector = node.properties.dataSnippetSelector as string;
	const attributeProperty = snippetSelector.at( 0 ) === '#' ? 'id' : 'className';
	const attributeValue = snippetSelector!.slice( 1 );

	if ( attributeProperty === 'className' ) {
		if ( !( 'className' in node.properties ) ) {
			node.properties.className = [];
		}

		( node.properties.className as Array<string> ).push( attributeValue );
	} else {
		node.properties.id = attributeValue;
	}
}

function getJsScriptTag( { selectedInstallMethod, selectedTechnology }: GetCodeHtmlOptions ) {
	const jsFilename = getFilename( { type: 'js', selectedTechnology } );

	const scriptTag: Record<string, string> = {};

	if ( selectedInstallMethod !== 'cdn' ) {
		scriptTag.type = 'module';
	}

	scriptTag.src = './' + jsFilename;

	return scriptTag;
}
